import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type SignupStep =
  | "required-term"
  | "signup-form"
  | "personal-sms"
  | "parent-required-term"
  | "parent-info-input"
  | "parent-kmc-verification"
  | "under-14-sms";

export interface SignupState {
  // Step 관리
  currentStep: SignupStep;

  // 회원가입 플로우 데이터
  termsAgreed: boolean;
  parentKmcAgreed: boolean;
  name: string;
  userId: string;
  password: string;
  birthDate: string;
  phoneNumber: string;
  isUnder14: boolean;

  // 보호자 정보 (14세 미만)
  parentInputName: string; // 사용자가 입력한 보호자 이름
  parentInputBirth: string; // 사용자가 입력한 보호자 생년월일 (YYYYMMDD)
  parentCertNum: string; // KMC 인증 요청 번호

  // Step Actions
  setStep: (step: SignupStep) => void;
  goToNextStep: () => void;
  goToPrevStep: () => void;

  // Data Actions
  setTermsAgreed: (agreed: boolean) => void;
  setParentKmcAgreed: (agreed: boolean) => void;
  setUserInfo: (info: {
    name: string;
    userId: string;
    password: string;
    birthDate: string;
  }) => void;
  setIsUnder14: (isUnder14: boolean) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setParentInfo: (info: { parentInputName: string; parentInputBirth: string }) => void;
  setParentCertNum: (certNum: string) => void;
  clearSignup: () => void;
}

const initialState = {
  currentStep: "required-term" as SignupStep,
  termsAgreed: false,
  parentKmcAgreed: false,
  name: "",
  userId: "",
  password: "",
  birthDate: "",
  phoneNumber: "",
  isUnder14: false,
  parentInputName: "",
  parentInputBirth: "",
  parentCertNum: "",
};

// Step 순서 정의 (14세 이상)
const ADULT_STEPS: SignupStep[] = ["required-term", "signup-form", "personal-sms"];

// Step 순서 정의 (14세 미만)
const MINOR_STEPS: SignupStep[] = [
  "required-term",
  "signup-form",
  "parent-required-term",
  "parent-info-input",
  "parent-kmc-verification",
  "under-14-sms",
];

const getNextStep = (currentStep: SignupStep, isUnder14: boolean): SignupStep | null => {
  const steps = isUnder14 ? MINOR_STEPS : ADULT_STEPS;
  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex === -1 || currentIndex === steps.length - 1) return null;
  return steps[currentIndex + 1];
};

const getPrevStep = (currentStep: SignupStep, isUnder14: boolean): SignupStep | null => {
  const steps = isUnder14 ? MINOR_STEPS : ADULT_STEPS;
  const currentIndex = steps.indexOf(currentStep);
  if (currentIndex <= 0) return null;
  return steps[currentIndex - 1];
};

export const useSignupStore = create<SignupState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Step Actions
      setStep: (step) => set({ currentStep: step }),

      goToNextStep: () => {
        const { currentStep, isUnder14 } = get();
        const nextStep = getNextStep(currentStep, isUnder14);
        if (nextStep) {
          set({ currentStep: nextStep });
        }
      },

      goToPrevStep: () => {
        const { currentStep, isUnder14 } = get();
        const prevStep = getPrevStep(currentStep, isUnder14);
        if (prevStep) {
          set({ currentStep: prevStep });
        }
      },

      // Data Actions
      setTermsAgreed: (agreed) => set({ termsAgreed: agreed }),
      setParentKmcAgreed: (agreed) => set({ parentKmcAgreed: agreed }),

      setUserInfo: (info) =>
        set({
          name: info.name,
          userId: info.userId,
          password: info.password,
          birthDate: info.birthDate,
        }),

      setIsUnder14: (isUnder14) => set({ isUnder14 }),

      setPhoneNumber: (phoneNumber) => set({ phoneNumber }),

      setParentInfo: (info) =>
        set({
          parentInputName: info.parentInputName,
          parentInputBirth: info.parentInputBirth,
        }),

      setParentCertNum: (certNum) => set({ parentCertNum: certNum }),

      clearSignup: () => set(initialState),
    }),
    {
      name: "growing-student-signup-storage",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
